import { uiText, useUiLanguage } from '@/i18n/interface';

import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { FileText } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { useDojoSettings } from '@/hooks/useDojoSettings';
import { useEffectiveDojo } from '@/hooks/useEffectiveDojo';
import { processTaxReportData } from '@/utils/taxReportDataProcessor';
import { generateTaxReportPdf } from '@/utils/taxReportPdfGenerator';
import { ReportPeriod } from '@/types/taxReport';
import TaxReportDialog from './TaxReportDialog';
import { supabase } from '@/integrations/supabase/client';

const TaxReportGenerator: React.FC = () => {
  useUiLanguage();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportPeriod, setReportPeriod] = useState<ReportPeriod>({
    startDate: `${new Date().getFullYear()}-01-01`,
    endDate: `${new Date().getFullYear()}-12-31`
  });
  const { toast } = useToast();
  const { settings } = useDojoSettings();
  const defaultCurrency = 'CHF'; // Default currency for tax reporting
  const { effectiveDojoId } = useEffectiveDojo();

  const handleGenerateTaxReport = async () => {
    setIsGenerating(true);
    try {
      if (!effectiveDojoId) throw new Error(uiText("Select a dojo before generating a tax report."));
      const reportData = await processTaxReportData(reportPeriod, effectiveDojoId);

      // Get the most commonly used currency from payment categories
      const { data: categories, error: categoriesError } = await supabase
        .from('payment_categories')
        .select('currency')
        .eq('dojo_id', effectiveDojoId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1);
      if (categoriesError) throw categoriesError;

      const dynamicCurrency = categories?.[0]?.currency || defaultCurrency;

      await generateTaxReportPdf(reportData, reportPeriod, settings.dojo_name, dynamicCurrency);

      toast({
        title: uiText("Success"),
        description: uiText("Tax report generated and downloaded successfully")
      });

      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error generating tax report:', error);
      toast({
        title: uiText("Error"),
        description: error instanceof Error ? error.message : "Failed to generate tax report",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setIsDialogOpen(true)}>
        <FileText className="h-4 w-4 mr-2" />
        {uiText("Tax Report")}</Button>
      <TaxReportDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        reportPeriod={reportPeriod}
        onReportPeriodChange={setReportPeriod}
        onGenerate={handleGenerateTaxReport}
        isGenerating={isGenerating}
      />
    </>
  );
};

export default TaxReportGenerator;
